import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useFeature } from '@growthbook/growthbook-react';
import { AppBar, Toolbar, Divider, Drawer, Box, Stack, Typography, IconButton, Button, ListItemButton } from '../ui/core';
import HitchLogoIcon from '../ui/icons/HitchLogo';
import HamburgerIcon from '../ui/icons/HamburgerIcon';
import {
  CancelIcon,
  CarCircleIcon,
  CarIcon,
  CheckCircleIcon,
  DollarBadgeIcon,
  LogoutIcon,
  MailIcon,
  PersonIcon,
  RideIcon,
  RouteIcon,
  SettingsIcon,
  StationIcon,
  WarningIcon,
} from '../ui/icons';
import { styled, scrollY } from '../ui/styles';
import { useAuth, useEnvironment } from '../hooks';
import { useHasPermission } from '../helpers/api/auth/index';
import { PERMISSIONS } from '../helpers/api/auth/permissions';
import { DriverStatusOptions } from '../models/Drivers';

const drawerWidth = 256;

// ── Flush chrome ──────────────────────────────────────────
// `flush` pages swap the top AppBar + persistent drawer for a slim icon rail
// pinned to the far left that expands to labels on hover, so the nav, a page's
// own secondary rail, and its content read as one surface. The rail overlays
// content when expanded (fixed position) rather than reflowing it — the same
// "master nav pops up" behaviour.
const RAIL_W = 68; // collapsed: just icons
// 68px is 18% of a 375px screen, permanently, for a strip of icons. 52 is the same
// icon at the smaller gutter a phone uses everywhere else, and hands 16px back to the
// content — which on this page is the difference between a card that fits and one that
// clips. Applied as a breakpoint pair rather than a new constant everywhere: the rail's
// own width and the content's left margin have to agree, so they both read from here.
const RAIL_W_XS = 52;
const railWidthSx = { xs: `${RAIL_W_XS}px`, sm: `${RAIL_W}px` };
const RAIL_W_OPEN = 236; // hovered: floating panel with icons + labels — healthy, not oversized
const ICON_COL = 40; // fixed icon column so icons stay centered when collapsed and labels align when open
// Below `md` there is no hover, and a 68px column of unlabelled icons is a third of a
// phone's width — so the rail is replaced by a slim top bar plus an overlay drawer.
// The bar is sticky, so anything that sticks BELOW it has to offset by its height —
// exported rather than re-declared per page so the two can't drift apart.
export const MOBILE_BAR_H = 56;
const MOBILE_DRAWER_W = 268;

// Expanded/collapsed lives at module scope so it SURVIVES the rail's remount on
// every page navigation. Driving expansion from React state (not CSS :hover)
// means the remounted rail starts already-expanded when the cursor is still
// over it — no flicker closed → reopen. It collapses only on a real mouseleave.
let railOpenPersist = false;

const railSurface = (theme) => theme.palette.background.paper;
const railHover = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.045)');
const railBorder = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.07)');
const activeBorder = (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.10)');

const iconColSx = { width: `${ICON_COL}px`, display: 'flex', justifyContent: 'center', flexShrink: 0 };

// The brand lockup — hand symbol + "hitch" wordmark, split into two pieces cropped
// from the SAME lockup coordinate space at the SAME scale, so together they reproduce
// the Home header logo exactly. Both slices keep the full 0-577 vertical frame and are
// top-aligned, which preserves the artwork's baseline relationship; the 7.4px gap is
// the artwork's own 284/577 spacing at this height. Module scope so the desktop rail,
// the mobile top bar and the mobile drawer all render one lockup instead of three.
const HitchSymbol = () => (
  <Box
    component="svg"
    viewBox="0 0 877.27 577"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ height: '15px', width: 'auto', display: 'block', color: 'text.primary', flexShrink: 0 }}
  >
    <g transform="scale(28.5755)">
      <path d="M18.6813 0.740441C19.6929 -0.246814 21.333 -0.246814 22.3444 0.740441L22.5641 0.95495L17.4359 5.96018C17.4334 5.96266 17.4307 5.96534 17.428 5.96777C17.3595 6.03382 17.286 6.09163 17.2084 6.14159C16.6453 6.5044 15.8803 6.44392 15.3845 5.96018C15.3138 5.89107 15.2518 5.81661 15.1987 5.73811C14.827 5.18861 14.8888 4.44185 15.3845 3.95809L18.6813 0.740441Z" />
      <path d="M23.5905 1.95593L25.6418 3.95804L20.5135 8.96327C19.9471 9.51613 19.0286 9.51613 18.4622 8.96327C18.3914 8.89418 18.3294 8.81967 18.2763 8.7412C17.9065 8.19444 17.9659 7.45239 18.4547 6.96847L18.4557 6.96749C18.4579 6.96538 18.4601 6.96327 18.4622 6.96117L23.5905 1.95593Z" />
      <path d="M28.7187 6.96121L26.6674 4.95911L21.5392 9.96437C20.9726 10.5172 20.9726 11.4136 21.5392 11.9665C21.6099 12.0356 21.6862 12.096 21.7666 12.1479C22.3281 12.5097 23.0906 12.4505 23.5863 11.9705C23.5877 11.9691 23.5892 11.9678 23.5905 11.9665L28.7187 6.96121Z" />
      <path d="M29.745 7.96228L29.9201 8.1332C30.9564 9.14454 30.9564 10.7842 29.9201 11.7956L26.668 14.9696C26.5179 15.1163 26.3428 15.2241 26.1561 15.2928C26.1229 15.3052 26.0894 15.3162 26.0555 15.326C25.5623 15.4684 25.007 15.3499 24.6175 14.9704L24.6167 14.9696C24.0511 14.4176 24.0505 13.5228 24.6146 12.9697L29.745 7.96228Z" />
      <path d="M14.3593 6.96119C13.8339 6.44839 13.5521 5.78787 13.5141 5.11661L11.9661 6.62742C10.8331 7.73316 8.99644 7.73316 7.8634 6.62742L7.17974 5.96016C5.48038 4.30153 2.72519 4.30153 1.02582 5.96016L0.00012207 6.96119L1.02163 7.95822L1.02582 7.96223L10.2565 16.9716C14.2217 20.8417 20.6505 20.8417 24.6158 16.9716L24.8901 16.7038C24.4139 16.5797 23.9639 16.3353 23.5902 15.9705C22.9776 15.3727 22.6963 14.5738 22.7463 13.7913C21.9444 13.84 21.1258 13.5654 20.5133 12.9675C19.9005 12.3695 19.6192 11.5706 19.6692 10.7881C18.8675 10.8369 18.0489 10.5623 17.4363 9.96433C16.8236 9.36634 16.5422 8.56745 16.5923 7.78498C15.7905 7.83374 14.972 7.55915 14.3593 6.96119Z" />
    </g>
  </Box>
);

// `className` defaults to the rail's "navLabel" so the wordmark fades out with the
// labels when the rail collapses; the mobile chrome passes nothing and keeps it shown.
const HitchWordmark = ({ className = 'navLabel' }) => (
  <Box
    component="svg"
    className={className}
    viewBox="1356 0 1910 577"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    sx={{ height: '15px', width: 'auto', display: 'block', color: 'text.primary', flexShrink: 0 }}
  >
    <path d="M3265.33 332.24V559.47C3265.33 564.22 3261.47 568.08 3256.72 568.08H3208.13C3203.38 568.08 3199.52 564.22 3199.52 559.47V339.66C3199.52 267.74 3141.21 209.43 3069.29 209.43H3058.07C2998.66 209.43 2948.55 249.21 2932.91 303.58C2932.8 303.95 2932.7 304.31 2932.59 304.68C2929.18 316.57 2927.38 329.42 2927.38 343.03V559.46C2927.38 564.21 2923.52 568.07 2918.77 568.07H2873.03C2868.28 568.07 2864.42 564.21 2864.42 559.46V8.61002C2864.42 3.86002 2868.28 0 2873.03 0H2917.76C2922.51 0 2926.37 3.86002 2926.37 8.61002V222.17C2945.94 194.94 2972.4 175.01 3003.97 163.09C3026.76 154.47 3052.22 150.03 3079.63 150.03C3107.04 150.03 3132.97 154.57 3155.63 163.09C3178.13 171.55 3197.71 183.91 3213.48 199.63C3214.29 200.41 3215.09 201.2 3215.87 201.99C3226.88 213.09 3235.73 225.92 3243.02 239.82C3257.95 268.27 3265.3 300.09 3265.3 332.22H3265.31L3265.33 332.24Z" />
    <path d="M1756.91 332.24V559.47C1756.91 564.22 1753.05 568.08 1748.3 568.08H1699.71C1694.96 568.08 1691.1 564.22 1691.1 559.47V339.66C1691.1 267.74 1632.79 209.43 1560.87 209.43H1549.65C1490.24 209.43 1440.13 249.21 1424.49 303.58C1424.38 303.95 1424.28 304.31 1424.17 304.68C1420.76 316.57 1418.96 329.42 1418.96 343.03V559.46C1418.96 564.21 1415.1 568.07 1410.35 568.07H1364.61C1359.86 568.07 1356 564.21 1356 559.46V8.61002C1356 3.86002 1359.86 0 1364.61 0H1409.34C1414.09 0 1417.95 3.86002 1417.95 8.61002V222.17C1437.52 194.94 1463.98 175.01 1495.55 163.09C1518.34 154.47 1543.8 150.03 1571.21 150.03C1598.62 150.03 1624.55 154.57 1647.21 163.09C1669.71 171.55 1689.29 183.91 1705.06 199.63C1705.87 200.41 1706.67 201.2 1707.45 201.99C1718.46 213.09 1727.31 225.92 1734.6 239.82C1749.53 268.27 1756.88 300.09 1756.88 332.22H1756.89L1756.91 332.24Z" />
    <path d="M2731.64 434.13L2766.07 445.97C2771.68 447.9 2774.41 454.27 2771.93 459.67C2739.29 530.52 2670.53 574.43 2582.62 576.21C2456.21 578.8 2362.19 490.02 2359.74 370.21C2357.3 250.41 2447.62 157.87 2574.02 155.3C2672.09 153.29 2753.62 204.05 2777.57 282.32C2779.38 288.22 2775.55 294.35 2769.44 295.25L2732.71 300.63C2728.43 301.26 2724.18 299.09 2722.28 295.21C2696.66 242.74 2642.76 206.6 2580.42 206.62C2493.15 206.65 2421.92 278.62 2422.74 365.87C2423.55 452.29 2493.85 522.1 2580.46 522.1C2640.65 522.1 2692.95 488.4 2719.55 438.85C2721.86 434.53 2726.99 432.56 2731.63 434.15H2731.62L2731.64 434.13Z" />
    <path d="M2250.04 513.88C2231.16 511.73 2213.23 503.49 2199.03 489.95C2181.32 473.06 2171.74 449.35 2171.74 424.88V221.95H2297.04C2301.94 221.95 2305.91 217.98 2305.91 213.08V168.89C2305.91 163.99 2301.94 160.02 2297.04 160.02H2171.74V76.4999C2171.74 71.5999 2167.77 67.6299 2162.87 67.6299H2118.68C2113.78 67.6299 2109.81 71.5999 2109.81 76.4999V160.02H2033.19C2028.29 160.02 2024.32 163.99 2024.32 168.89V213.08C2024.32 217.98 2028.29 221.95 2033.19 221.95H2109.81V426.1C2109.81 470.5 2129.28 512.39 2163.24 541.02C2190.59 564.08 2224.83 576.41 2259.99 576.41C2268.48 576.41 2277.03 575.69 2285.54 574.22L2307.71 570.42C2311.97 569.69 2315.08 566 2315.08 561.67V520.09C2315.08 514.48 2309.92 510.27 2304.42 511.39C2283.11 515.78 2265.16 516.17 2250.06 513.88H2250.04Z" />
    <path d="M1909.36 93.0804C1934.93 93.0804 1955.66 72.3512 1955.66 46.7805C1955.66 21.2097 1934.93 0.480469 1909.36 0.480469C1883.79 0.480469 1863.06 21.2097 1863.06 46.7805C1863.06 72.3512 1883.79 93.0804 1909.36 93.0804Z" />
    <path d="M1926.67 163.091H1892.05C1884.51 163.091 1878.39 169.207 1878.39 176.751V554.401C1878.39 561.945 1884.51 568.061 1892.05 568.061H1926.67C1934.21 568.061 1940.33 561.945 1940.33 554.401V176.751C1940.33 169.207 1934.21 163.091 1926.67 163.091Z" />
  </Box>
);

/**
 * Whether a path is inside a nav section, matched on SEGMENT boundaries.
 *
 * Not a raw `startsWith`: `/drivers` is a prefix of `/drivers-approval`, so a plain
 * prefix test lit both the Drivers and the Driver Approvals items at once for every
 * page in the approvals section. Any two sections whose names share a stem would
 * collide the same way.
 */
export const matchesSection = (pathname, base) => pathname === base || pathname.startsWith(`${base}/`);

// Nav rows: comfortable height with a larger icon + label. The current page
// reads as a raised white chip (hairline border + soft shadow) the way
// a selected item does, rather than a flat grey fill.
const railItemSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  // MUI's ListItemButton sets flex-grow:1, which would stretch each row to fill
  // the column and spread the items apart — pin them to their own height.
  flexGrow: 0,
  flexShrink: 0,
  gap: '12px',
  height: '38px',
  minHeight: '38px',
  px: 0,
  py: 0,
  mb: '2px',
  borderRadius: '9px',
  color: (theme) => theme.palette.text.primary,
  textDecoration: 'none',
  transition: 'background-color 0.12s ease, box-shadow 0.12s ease',
  '& .MuiSvgIcon-root': { fontSize: 18, flexShrink: 0 },
  '& .navLabel': { fontSize: '14px', fontWeight: 500 },
  '&:hover': { backgroundColor: railHover },
  '&.active': {
    backgroundColor: (theme) => theme.palette.background.paper,
    boxShadow: (theme) => `inset 0 0 0 1px ${activeBorder(theme)}, 0 1px 2px rgba(0,0,0,0.04)`,
    '& .navLabel': { fontWeight: 600 },
    '&:hover': { backgroundColor: (theme) => theme.palette.background.paper },
  },
};

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' && prop !== 'flush' })(
  ({ theme, open, flush }) => ({
    flexGrow: 1,
    minWidth: 0,
    // `flush` pages (e.g. Promotions) manage their own padding so the page can
    // read as one continuous surface with the nav rather than a padded box.
    padding: flush ? 0 : theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: 0,
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: drawerWidth,
    }),
  }),
);

const Spacer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  height: '84px',
}));

const Nav = styled('nav')(({ theme }) => ({
  paddingTop: theme.spacing(3),
  display: 'flex',
  flexDirection: 'column',
  a: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 12px',
    ...theme.typography.body2,
    color: theme.palette.text.secondary,
    '&.active-parent': {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      fontWeight: 700,
    },
    '&.active': {
      ...theme.typography.body1,
      color: theme.palette.text.primary,
      fontWeight: 700,
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.palette.background.default,
    },
  },
  '> div': {
    a: {
      paddingLeft: '32px',
    },
  },
}));

const DashboardLayout = ({ title, avatar, appBarContent, children, flush = false }) => {
  const auth = useAuth();
  // On classic pages the primary nav is a persistent drawer, open by default.
  const [drawerOpen, setDrawerOpen] = useState(true);
  // Rail expansion is JS state seeded from the module-scoped persisted value, so
  // navigating (which remounts this layout) doesn't flicker the rail closed and
  // back open — it stays expanded while the cursor remains over it, and only
  // collapses when the cursor actually leaves.
  const [railExpanded, setRailExpanded] = useState(railOpenPersist);
  // The mobile overlay nav. Separate from `railExpanded`: that one is hover-driven
  // and persists across remounts, this one is an explicit open/close.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const expandRail = () => {
    railOpenPersist = true;
    setRailExpanded(true);
  };
  const collapseRail = () => {
    railOpenPersist = false;
    setRailExpanded(false);
  };
  const { environmentBackgroundColor } = useEnvironment();
  const hasChangeDriverStatusPermission = useHasPermission(PERMISSIONS.CHANGE_DRIVER_STATUS);
  const hasSendNotificationsPermission = useHasPermission(PERMISSIONS.SEND_DRIVER_NOTIFICATIONS);
  const hasTripThrottlePermission = useHasPermission(PERMISSIONS.TRIP_THROTTLE);
  const showTripThrottling = useFeature('admin-trip-throttling').on;
  // No permission gate (unlike trip-throttling): any admin may manage
  // rider-driver exclusions for v1, so the nav item shows on the flag alone.
  const showRiderDriverExclusions = useFeature('admin-rider-driver-exclusions').on;
  // The routes enforce these too — see PrivateRoute — because hiding a nav item is not
  // access control. Both are satisfied by ADMIN, so a superuser sees them either way.
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  const canManageVehicles = useHasPermission(PERMISSIONS.MANAGE_VEHICLE_CATALOG);

  let navigation = [
    { label: 'Trips', link: '/trips/daily', icon: <RideIcon /> },
    { label: 'Users', link: '/users', icon: <PersonIcon /> },
    { label: 'Stations', link: '/stations', icon: <StationIcon /> },
    {
      label: 'Location',
      link: '/location/isochrones',
      // Keep the section highlighted across both map views.
      activeBase: '/location',
      icon: <RouteIcon />,
      sublinks: [
        { label: 'Isochrones', link: '/location/isochrones' },
        { label: 'Lane Map', link: '/location/lane-map' },
      ],
    },
    { label: 'Fleet', link: '/fleet-management', icon: <CarIcon /> },
    // Unconditional, no flag and no permission: read-only in v1, open to all admins.
    { label: 'Driver Quests', link: '/driver-quests', icon: <DollarBadgeIcon /> },
    { label: 'Promotions', link: '/promotions', icon: <DollarBadgeIcon /> },
  ];
  if (canManageVehicles) {
    // Its own tab, not a Fleet sublink: Fleet is Hitch-owned vehicles on the road,
    // this is the make/model/year catalog drivers pick from. Distinct icon so the
    // two aren't read as the same section.
    navigation.splice(5, 0, { label: 'Vehicles', link: '/vehicles', icon: <CarCircleIcon /> });
  }
  if (hasChangeDriverStatusPermission) {
    navigation.splice(2, 0, {
      label: 'Driver Approvals',
      link: '/drivers-approval/drivers-triage/waitlist',
      // Stay highlighted for the whole section, including a driver's profile
      // (/drivers-approval/funnel/:id) and every status tab — not just waitlist.
      activeBase: '/drivers-approval',
      icon: <CheckCircleIcon />,
      sublinks: DriverStatusOptions.map(({ label, value }) => ({
        label: (
          <>
            <span>{label}</span>
          </>
        ),
        link: `/drivers-approval/drivers-triage/${value}?page=1`,
      })),
    });
  }
  if (canManageDrivers) {
    // Sits beside Driver Approvals rather than under it: this is per-document work
    // and per-standing, not another cut of the application-status funnel. The
    // profile is listed too so opening a driver keeps this item highlighted.
    navigation.splice(hasChangeDriverStatusPermission ? 3 : 2, 0, {
      label: 'Drivers',
      link: '/drivers',
      activeBase: ['/drivers', '/driver-profile'],
      icon: <CheckCircleIcon />,
      // No sublinks: the flush rail these pages use renders label + icon only —
      // `sublinks` is read by the classic drawer layout alone, so listing them here
      // would be config that renders nowhere. The page's own tabs carry its sections.
    });
  }
  if (hasSendNotificationsPermission) {
    navigation.push({
      label: 'Notifications',
      link: '/notifications/drivers',
      icon: <MailIcon />,
    });
  }
  if (showTripThrottling && hasTripThrottlePermission) {
    navigation.push({
      label: 'New Trip Throttling',
      link: '/trip-throttling',
      icon: <WarningIcon />,
    });
  }
  if (showRiderDriverExclusions) {
    navigation.push({
      label: 'Rider-Driver Exclusions',
      link: '/rider-driver-exclusions',
      icon: <CancelIcon />,
    });
  }
  // HITCH-5500. No flag or permission gate: ownership itself is the GrowthBook
  // `sdp.config` flag (shown read-only on the page), and the admin API's
  // AdminGuard covers the page's writes the same way it does every admin route.
  navigation.push({
    label: 'Supply-Demand Pace',
    link: '/supply-demand-pace',
    icon: <SettingsIcon />,
  });

  const onLogout = () => auth.logout();

  // ── Flush layout ──────────────────────────────────────────────────────────
  // Desktop: the slim icon rail + page-owned content.
  // Below `md`: a slim top bar (hamburger + brand + page title) and an overlay
  // drawer, because touch has no hover to expand the rail with and a permanent
  // 68px icon column costs a phone a fifth of its width.
  if (flush) {
    const navItem = (item, { showLabel }) => (
      <ListItemButton
        key={item.link}
        component={NavLink}
        to={item.link}
        activeClassName="active"
        onClick={() => setMobileNavOpen(false)}
        isActive={
          item.activeBase
            ? (_match, location) =>
                // Array or string, so a section spread over sibling paths
                // can list them instead of relying on a shared prefix.
                [].concat(item.activeBase).some((base) => matchesSection(location.pathname, base))
            : undefined
        }
        disableRipple
        sx={railItemSx}
      >
        <Box sx={iconColSx}>{item.icon}</Box>
        <Typography
          className={showLabel ? undefined : 'navLabel'}
          sx={{ fontSize: '14px', fontWeight: 'inherit', color: 'inherit' }}
        >
          {item.label}
        </Typography>
      </ListItemButton>
    );

    const logoutItem = ({ showLabel }) => (
      <ListItemButton onClick={onLogout} disableRipple sx={railItemSx}>
        <Box sx={iconColSx}>
          <LogoutIcon />
        </Box>
        <Typography className={showLabel ? undefined : 'navLabel'} sx={{ fontSize: '14px', color: 'inherit' }}>
          Logout
        </Typography>
      </ListItemButton>
    );

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {/* ── Mobile top bar ── */}
        <Box
          component="header"
          sx={{
            display: { xs: 'flex', md: 'none' },
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar,
            alignItems: 'center',
            gap: '10px',
            height: `${MOBILE_BAR_H}px`,
            flexShrink: 0,
            px: '8px',
            boxSizing: 'border-box',
            borderBottom: (theme) => `1px solid ${railBorder(theme)}`,
            // Same opaque-base + tint layering as the rail, so a staging build still
            // reads as staging and page content can't bleed through while scrolling.
            backgroundColor: railSurface,
            backgroundImage: environmentBackgroundColor
              ? `linear-gradient(${environmentBackgroundColor}, ${environmentBackgroundColor})`
              : 'none',
          }}
        >
          <IconButton aria-label="Open navigation" onClick={() => setMobileNavOpen(true)}>
            <HamburgerIcon sx={{ fontSize: 24 }} />
          </IconButton>
          <Link to="/home" onClick={collapseRail} style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
            <HitchSymbol />
            <HitchWordmark className={undefined} />
          </Link>
          {title && (
            <Typography
              sx={{
                fontSize: '15px',
                fontWeight: 600,
                color: 'text.secondary',
                ml: 'auto',
                pr: '8px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              {title}
            </Typography>
          )}
        </Box>

        {/* ── Mobile overlay nav ── */}
        <Drawer
          variant="temporary"
          open={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'none' } }}
          PaperProps={{
            sx: {
              width: `${MOBILE_DRAWER_W}px`,
              boxSizing: 'border-box',
              px: '14px',
              py: '14px',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: railSurface,
              backgroundImage: environmentBackgroundColor
                ? `linear-gradient(${environmentBackgroundColor}, ${environmentBackgroundColor})`
                : 'none',
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7.4px', height: '40px', mb: '4px', flexShrink: 0, pl: '6px' }}>
            <Link
              to="/home"
              onClick={() => {
                collapseRail();
                setMobileNavOpen(false);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}
            >
              <HitchSymbol />
              <HitchWordmark className={undefined} />
            </Link>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', ...scrollY, overflowX: 'hidden' }}>
            {navigation.map((item) => navItem(item, { showLabel: true }))}
          </Box>
          <Box sx={{ flexShrink: 0, pt: '8px' }}>{logoutItem({ showLabel: true })}</Box>
        </Drawer>

        {/* ── Desktop icon rail ── */}
        <Box
          component="nav"
          onMouseEnter={expandRail}
          onMouseLeave={collapseRail}
          sx={{
            display: { xs: 'none', md: 'flex' },
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            width: railExpanded ? `${RAIL_W_OPEN}px` : railWidthSx,
            boxSizing: 'border-box',
            px: { xs: '6px', sm: '14px' },
            py: '14px',
            flexDirection: 'column',
            // Staging/dev tint the rail (preserving the old AppBar signal), layered
            // over an always-opaque base. `environmentBackgroundColor` is
            // intentionally translucent (rgba) — that was fine tinting the old
            // thin AppBar, but this panel is a much larger fixed overlay, so a
            // translucent background would let the page content underneath
            // bleed through. Painting it as a backgroundImage on top of a solid
            // backgroundColor keeps the tint while guaranteeing opacity.
            backgroundColor: railSurface,
            backgroundImage: environmentBackgroundColor
              ? `linear-gradient(${environmentBackgroundColor}, ${environmentBackgroundColor})`
              : 'none',
            // Expanded: widen into a full-height floating panel flush to the left
            // edge, its right corners rounded and a soft shadow lifting it over the
            // content — the "master nav pops up" behaviour.
            borderRight: railExpanded ? 'none' : (theme) => `1px solid ${railBorder(theme)}`,
            borderRadius: railExpanded ? '0 18px 18px 0' : 0,
            boxShadow: railExpanded
              ? (theme) => (theme.palette.mode === 'dark' ? '12px 0 48px rgba(0,0,0,0.6)' : '12px 0 40px rgba(0,0,0,0.12)')
              : 'none',
            overflow: 'hidden',
            transition: 'width 0.2s cubic-bezier(0.4,0,0.2,1), border-radius 0.2s ease, box-shadow 0.2s ease',
            '& .navLabel': {
              opacity: railExpanded ? 1 : 0,
              whiteSpace: 'nowrap',
              pointerEvents: 'none',
              transition: 'opacity 0.12s ease',
            },
          }}
        >
          {/* Going Home leaves the rail behind (Home has no rail), so collapse and
              clear the persisted open-state — otherwise opening a page from a Home
              card would wrongly start expanded. */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '7.4px', height: '40px', mb: '4px', flexShrink: 0, pl: '6px' }}>
            <Link to="/home" onClick={collapseRail} style={{ display: 'flex', alignItems: 'center' }}>
              <HitchSymbol />
            </Link>
            <HitchWordmark />
          </Box>

          {/* Primary nav */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              ...scrollY,
              overflowX: 'hidden',
              // Collapsed shows no scroll thumb; only scroll once expanded.
              overflowY: railExpanded ? 'auto' : 'hidden',
            }}
          >
            {navigation.map((item) => navItem(item, { showLabel: false }))}
          </Box>

          {/* Utilities */}
          <Box sx={{ flexShrink: 0, pt: '8px' }}>{logoutItem({ showLabel: false })}</Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            // Only the desktop rail is a fixed overlay that content must clear.
            ml: { xs: 0, md: `${RAIL_W}px` },
            display: 'flex',
            // Mobile: the top bar has already taken its share, so the page fills what
            // is left rather than adding another 100vh below it.
            minHeight: { xs: 0, md: '100vh' },
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  // ── Classic layout: top AppBar + persistent drawer (all other pages) ──────
  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, backgroundColor: environmentBackgroundColor }}
        elevation={0}
        color="white"
      >
        <Toolbar>
          <Stack sx={{ mr: 11 }} direction="row" alignItems="center" spacing={4}>
            <Link to="/home">
              <HitchLogoIcon sx={{ fontSize: 40 }} />
            </Link>
            <Divider orientation="vertical" flexItem />
            {navigation && (
              <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
                <HamburgerIcon sx={{ fontSize: 32 }} />
              </IconButton>
            )}
          </Stack>
          <Stack sx={{ flex: 1 }} direction="row" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={20}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {avatar}
                <Typography variant="h1">{title}</Typography>
              </Stack>
              {appBarContent}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Button variant="outlined" onClick={onLogout}>
                Logout
              </Button>
            </Stack>
          </Stack>
        </Toolbar>
      </AppBar>

      {navigation && (
        <Drawer variant="persistent" open={drawerOpen}>
          <Spacer />
          <Box sx={{ overflow: 'auto', width: drawerWidth }}>
            <Nav>
              {navigation.map(({ label, link, sublinks }) => (
                <React.Fragment key={link}>
                  <NavLink activeClassName={sublinks && sublinks.length > 0 ? 'active-parent' : 'active'} to={link}>
                    {label}
                  </NavLink>
                  {sublinks && sublinks.length > 0 && (
                    <div>
                      {sublinks.map((i) => (
                        <NavLink exact key={i.link} to={i.link}>
                          {i.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              ))}
            </Nav>
          </Box>
        </Drawer>
      )}
      <Main open={drawerOpen} flush={flush}>
        <Spacer />
        {children}
      </Main>
    </Box>
  );
};

export default DashboardLayout;
